// Buyer Profiles for Sellaris
// Each profile represents a different buyer character with unique voice and display

import ryanCruzAvatar from "@/assets/ryan-cruz-avatar.jpg";
import michaelMorrisAvatar from "@/assets/michael-morris-avatar.png";
import christianCarrollAvatar from "@/assets/christian-carroll-avatar.png";
import danaMitchellAvatar from "@/assets/dana-mitchell-avatar.png";
import alexChenAvatar from "@/assets/alex-chen-avatar.png";
import jordanHayesAvatar from "@/assets/jordan-hayes-avatar.png";
import unhingedBuyerAvatar from "@/assets/unhinged-buyer-avatar.png";
import patWashingtonAvatar from "@/assets/pat-washington-avatar.jpg";
import kimYanAvatar from "@/assets/kim-yan-avatar.jpg";
import rafaelBonillaAvatar from "@/assets/rafael-bonilla-avatar.jpg";
import anneComparionAvatar from "@/assets/anne-comparion-avatar.jpg";
import albertMillerAvatar from "@/assets/albert-miller-avatar.jpg";

export interface BuyerProfile {
  id: string;
  displayName: string;
  voiceId: string;           // ElevenLabs voice ID
  openaiVoiceId: string;     // OpenAI voice ID
  description: string;
  stylePreset: "neutral" | "skeptical" | "direct" | "friendly" | "analytical" | "impatient" | "topg" | "unhinged" | "alien" | "mtsu-pat-washington" | "mtsu-kim-yan" | "mtsu-rafael-bonilla";
  avatar: string;
  avatarGlbUrl?: string;     // 3D GLB avatar for TalkingHead (optional)
  avatarBody?: 'M' | 'F';    // Body type for TalkingHead lip-sync
  difficulty: number;        // 1-5 difficulty level (read-only)
  testPhrase: string;        // Unique voice preview phrase
  detailedDescription: string;  // In-depth buyer personality description
  tips: string[];               // Tips for succeeding with this buyer
}

// Default voice ID (Brian) used as fallback
export const DEFAULT_VOICE_ID = "nPczCjzI2devNBz1zQrb";
export const DEFAULT_OPENAI_VOICE_ID = "ash"; // Realtime API supported voice

// Legacy constant for backwards compatibility
export const VOICE_TEST_PHRASE = "Hi there. Thanks for coming in. What can I do for you today?";

export const buyerProfiles: BuyerProfile[] = [
  {
    id: "ryan-cruz",
    displayName: "Ed Gogol",
    voiceId: "GS31rV7KHR4ZG159sN2G", // Custom Ed Gogol (ElevenLabs)
    openaiVoiceId: "ash",            // Warm, conversational (Realtime API)
    description: "Neutral / Standard",
    stylePreset: "neutral",
    avatar: ryanCruzAvatar,
    // avatarGlbUrl removed - file doesn't exist, will use fallback
    avatarBody: "M",
    difficulty: 3,
    testPhrase: "Good to meet you. I've got some time blocked off, so let's see what you've got.",
    detailedDescription: "Ed is a fair, balanced buyer who evaluates proposals on their merits. He won't volunteer information but responds well to good discovery questions. He's professional and expects you to lead the conversation effectively.",
    tips: [
      "Use strong SPIN questions to uncover needs — he won't offer info freely",
      "Stay structured and professional throughout",
      "Don't rush to pitch; earn the right by demonstrating understanding first",
      "Confirm understanding before presenting solutions"
    ],
  },
  {
    id: "michael-morris",
    displayName: "Michael Morris",
    voiceId: "cjVigY5qzO86Huf0OWal",  // ElevenLabs - Eric (warm, conversational male)
    openaiVoiceId: "echo",            // Clear, balanced (Realtime API)
    description: "Friendly / Approachable",
    stylePreset: "friendly",
    avatar: michaelMorrisAvatar,
    // avatarGlbUrl removed - file doesn't exist, will use fallback
    avatarBody: "M",
    difficulty: 1,
    testPhrase: "Hey! Come on in, grab a seat! I've been looking forward to this. Want some coffee?",
    detailedDescription: "Michael is warm, open, and genuinely wants to help you succeed. He's more willing to share information and give you hints when you're on the right track. Great for building confidence, but don't let his friendliness make you skip proper discovery.",
    tips: [
      "Build rapport naturally — he responds well to warmth",
      "Don't mistake friendliness for a closed deal — still do full discovery",
      "Use his openness to practice asking deeper implication questions",
      "Good buyer for practicing your overall flow and timing"
    ],
  },
  {
    id: "christian-carroll",
    displayName: "Christian Carroll",
    voiceId: "cR7KK4WttYVxvi87O3w9",  // ElevenLabs - Custom Christian Carroll
    openaiVoiceId: "ash",             // Deep, authoritative (Realtime API - was 'onyx', now 'ash')
    description: "Cocky & Direct",
    stylePreset: "topg",
    avatar: christianCarrollAvatar,
    // Custom GLB removed - Avatar SDK export lacks required "Armature" object for TalkingHead
    // To use custom avatar: process GLB with TalkingHead's rename-avatarsdk-shapekeys.py script
    avatarBody: "M",
    difficulty: 4,
    testPhrase: "Alright, you've got my attention. Don't waste it. Show me why I should care.",
    detailedDescription: "Christian is confident, direct, and has zero patience for fluff. He'll challenge weak points aggressively and may dismiss vague answers. He respects confidence and directness — show him you know your stuff and won't back down under pressure.",
    tips: [
      "Match his energy — be confident and direct, not submissive",
      "Back up every claim with specifics and data",
      "Don't apologize or hedge; own your statements",
      "If he pushes back, clarify and re-address without getting defensive"
    ],
  },
  {
    id: "dana-mitchell",
    displayName: "Dana Mitchell",
    voiceId: "EXAVITQu4vr4xnSDxMaL",  // ElevenLabs - Sarah
    openaiVoiceId: "sage",            // Calm, measured (Realtime API)
    description: "Analytical / Detail-Oriented",
    stylePreset: "analytical",
    avatar: danaMitchellAvatar,
    // avatarGlbUrl removed - file doesn't exist, will use fallback
    avatarBody: "F",
    difficulty: 4,
    testPhrase: "I've reviewed the preliminary data you sent. I have seventeen questions. Let's start with methodology.",
    detailedDescription: "Dana is methodical, precise, and data-driven. She'll ask detailed technical questions and expects thorough, accurate answers. Vague responses frustrate her. She appreciates when you admit what you don't know rather than making things up.",
    tips: [
      "Be precise with numbers, specs, and timelines",
      "If you don't know something, say so and offer to follow up",
      "Avoid marketing fluff — stick to facts and evidence",
      "Take your time; she values accuracy over speed"
    ],
  },
  {
    id: "dr-alex-chen",
    displayName: "Dr. Alex Chen",
    voiceId: "onwK4e9ZLuTAKqWW03F9",  // ElevenLabs - Daniel
    openaiVoiceId: "echo",            // Male, clear voice (Realtime API)
    description: "Skeptical / Budget-Conscious",
    stylePreset: "skeptical",
    avatar: alexChenAvatar,
    // avatarGlbUrl removed - file doesn't exist, will use fallback
    avatarBody: "M",
    difficulty: 3,
    testPhrase: "I'll be honest with you — we've been burned by vendors before. Convince me this is different.",
    detailedDescription: "Dr. Chen has been burned by vendors before and approaches every pitch with healthy skepticism. He's particularly focused on ROI, risk mitigation, and proof. You'll need to earn his trust through solid evidence and acknowledging his concerns rather than dismissing them.",
    tips: [
      "Acknowledge his past experiences before presenting solutions",
      "Lead with ROI and cost justification — he's budget-focused",
      "Provide proof points, case studies, or references when possible",
      "Use the Clarify step in objections — he wants to feel heard"
    ],
  },
  {
    id: "jordan-hayes",
    displayName: "Jordan Hayes",
    voiceId: "GBAVqyaCjbTPNNPr1yUw",  // ElevenLabs - Custom Jordan Hayes
    openaiVoiceId: "ash",             // Male, authoritative (Realtime API)
    description: "Busy / Impatient Executive",
    stylePreset: "impatient",
    avatar: jordanHayesAvatar,
    // avatarGlbUrl removed - file doesn't exist, will use fallback
    avatarBody: "M",
    difficulty: 5,
    testPhrase: "I have a meeting with the Board in 15 minutes. This better be worth my time.",
    detailedDescription: "Jordan is a C-suite executive with zero tolerance for wasted time. He'll cut you off if you ramble and expects you to get to value fast. He makes quick decisions when convinced, but you have a narrow window to make your case.",
    tips: [
      "Lead with the bottom line — what's the impact?",
      "Keep responses concise; avoid over-explaining",
      "Prioritize the 2-3 most compelling points, not everything",
      "If interrupted, adapt quickly and address what he asks"
    ],
  },
  {
    id: "unhinged-buyer",
    displayName: "Unhinged",
    voiceId: "TX3LPaxmHKxFdv7VOQHJ",  // ElevenLabs - Liam (energetic)
    openaiVoiceId: "echo",
    description: "Will roast you",
    stylePreset: "unhinged",
    avatar: unhingedBuyerAvatar,
    // avatarGlbUrl removed - file doesn't exist, will use fallback
    avatarBody: "M",
    difficulty: 5,
    testPhrase: "Oh great, another salesperson. Let me guess — you're gonna change my life, right? This should be entertaining.",
    detailedDescription: "This buyer is chaotic, unpredictable, and will roast you mercilessly for fun. They'll throw curveballs, mock weak answers, and generally test your composure. Not for the faint of heart — but great for stress inoculation and learning to handle the unexpected.",
    tips: [
      "Don't take anything personally — it's all part of the game",
      "Stay calm and composed no matter what they throw at you",
      "Use humor back if you can, but stay professional",
      "Great practice for handling hostile or difficult buyers"
    ],
  },
  {
    id: "alien",
    displayName: "Alien",
    voiceId: "onwK4e9ZLuTAKqWW03F9",  // ElevenLabs - Daniel (robotic/precise)
    openaiVoiceId: "alloy",           // Neutral, precise (Realtime API)
    description: "Confused Extraterrestrial",
    stylePreset: "alien",
    avatar: "",  // Will show "A" initials
    avatarBody: "M",
    difficulty: 5,
    testPhrase: "Greetings, Earth creature. I am told you wish to exchange... matter? This concept confuses me.",
    detailedDescription: "This alien crash-landed on Earth 47 minutes ago and understands nothing about human commerce, money, biology, or social customs. They take everything literally, offer payment in antimatter or compressed time, and are genuinely horrified by concepts like 'eating' or 'working.'",
    tips: [
      "Explain concepts in simple, literal terms — no idioms or metaphors",
      "Be patient when they offer absurd forms of payment",
      "Don't get frustrated by philosophical tangents about capitalism",
      "Success = getting them to understand AND agree to any form of trade"
    ],
  },
  // --- MTSU / NTC Scenario-Specific Personas ---
  {
    id: "mtsu-pat-washington",
    displayName: "Pat Washington",
    voiceId: "EXAVITQu4vr4xnSDxMaL",  // ElevenLabs - Sarah (calm, professional)
    openaiVoiceId: "sage",
    description: "Senior Accounting Manager — Risk-Averse",
    stylePreset: "mtsu-pat-washington" as const,
    avatar: patWashingtonAvatar,
    avatarBody: "F" as const,
    difficulty: 3,
    testPhrase: "Thanks for coming in. I'll be honest, I'm cautious about change — but I did read that whitepaper, and it caught my attention.",
    detailedDescription: "Pat has 15 years in supply chain and 10 at Cumberland. She's professional, calm, and cautious. She values accuracy, minimal disruption, and audit-ready processes. Not technical — she thinks in business outcomes. She fears system changes but was intrigued by a whitepaper on cycle counting.",
    tips: [
      "Build trust before pitching — she needs to feel safe",
      "Focus on business outcomes, not technical features",
      "Acknowledge the risk of change; show how to minimize disruption",
      "Reference the whitepaper she read to establish credibility"
    ],
  },
  {
    id: "mtsu-kim-yan",
    displayName: "Kim Yan",
    voiceId: "onwK4e9ZLuTAKqWW03F9",  // ElevenLabs - Daniel
    openaiVoiceId: "echo",
    description: "Technology Manager — Practical & Direct",
    stylePreset: "mtsu-kim-yan" as const,
    avatar: kimYanAvatar,
    avatarBody: "M" as const,
    difficulty: 4,
    testPhrase: "Look, my team's already overwhelmed from the merger. If this adds more work, I'm not interested.",
    detailedDescription: "Kim is direct, practical, and skeptical but fair. He manages a team of 5 and is overwhelmed after the merger expanded from 8 to 28 locations. He always thinks about workload — who does the work, how much training, will this reduce or increase his team's burden. Opposed to uncontrolled projects.",
    tips: [
      "Lead with how it reduces workload, not adds features",
      "Be specific about implementation effort and training time",
      "Don't oversell — he'll respect honesty about challenges",
      "Show you understand his team is already stretched thin"
    ],
  },
  {
    id: "mtsu-rafael-bonilla",
    displayName: "Rafael Bonilla",
    voiceId: "cR7KK4WttYVxvi87O3w9",  // ElevenLabs - authoritative
    openaiVoiceId: "ash",
    description: "Finance Manager — Analytical & ROI-Driven",
    stylePreset: "mtsu-rafael-bonilla" as const,
    avatar: rafaelBonillaAvatar,
    avatarBody: "M" as const,
    difficulty: 4,
    testPhrase: "I appreciate the enthusiasm, but I need to see measurable impact. Walk me through the numbers.",
    detailedDescription: "Rafael is calm, analytical, and ROI-driven. He's the final approver and filters excitement through reality. He needs measurable impact, risk assessment, and a staged implementation plan. Not supply chain savvy — he thinks in financial terms. Skeptical of vague promises.",
    tips: [
      "Lead with ROI and measurable business impact",
      "Present a staged implementation plan with clear milestones",
      "Quantify risk reduction, not just feature benefits",
      "Keep it concise — he values logical, structured arguments"
    ],
  },
  // --- Comparion (MTSU MKT 3840) Persona ---
  {
    id: "anne-comparion",
    displayName: "Anne",
    voiceId: "EXAVITQu4vr4xnSDxMaL",  // ElevenLabs - Sarah (warm, friendly female)
    openaiVoiceId: "shimmer",          // Realtime API - warm female
    description: "Warm small-business owner",
    stylePreset: "friendly" as const,
    avatar: anneComparionAvatar,
    avatarBody: "F" as const,
    difficulty: 2,
    testPhrase: "Hey, good to see you again! Things at the bakery are wild — what did you want to talk about?",
    detailedDescription: "Anne is a 30-something single mom of two and owner of Anne's Sweets, a ~3-year-old small bakery. She's an existing Comparion client (auto + renters) and trusts you personally. Warm, optimistic, and conversational — but slightly avoidant when the conversation turns to mortality or worst-case scenarios. She'll joke or deflect under pressure. She opens up once rapport is real and only commits to the L.I.F.E. need when she has helped build it herself.",
    tips: [
      "Lead with rapport — ask about the bakery and her kids before any heavy questions",
      "Stay in Implication longer than feels comfortable — that's where she connects emotionally",
      "Build the L.I.F.E. picture WITH her; never present numbers at her",
      "Expect 2–4 deflections ('I'm young', 'too expensive', 'I'll think about it') — acknowledge, then re-anchor on her kids and business",
      "Close on the need, not a product; ask for a follow-up and referrals"
    ],
  },
  // --- Purdue Ryerson / Norden Persona ---
  {
    id: "albert-miller",
    displayName: "Albert Miller",
    voiceId: "nPczCjzI2devNBz1zQrb",  // ElevenLabs - Brian (deep, professional male)
    openaiVoiceId: "ash",
    description: "Fabrication Manager — Norden Mfg.",
    stylePreset: "neutral",
    avatar: albertMillerAvatar,
    avatarBody: "M",
    difficulty: 3,
    testPhrase: "Good to see you. Let me show you around the shop floor before we sit down.",
    detailedDescription: "Albert is the Fabrication Manager at Norden Manufacturing in North Bloomfield, OH. Practical, hands-on, and proud of his shop. He'll happily talk about the operation during a walkthrough but expects you to notice opportunities on your own. Fair, professional, and open to a real conversation with a rep who has done their homework.",
    tips: [
      "Reference specifics from the case PDF — Albert respects preparation",
      "Ask about the full operation, not just what Ryerson already sells them",
      "Uncover incremental opportunities (tubing, processing, cut-to-spec)",
      "Confirm next steps before leaving the office",
    ],
  },
];

// Get a profile by ID, returns first profile if not found
export const getBuyerProfile = (id: string): BuyerProfile => {
  return buyerProfiles.find(p => p.id === id) || buyerProfiles[0];
};

// Convert a saved buyer persona (from DB) to a BuyerProfile for use in sessions
export const savedPersonaToProfile = (persona: {
  id: string;
  name: string;
  disc_profile: {
    primary_type: string;
    buyer_insight?: string;
    selling_recommendations?: string[];
    watch_out_for?: string[];
    job_title?: string;
    company?: string;
  };
  style_preset: string;
  openai_voice_id: string;
}): BuyerProfile => {
  const DISC_DIFFICULTY_MAP: Record<string, number> = { I: 2, S: 3, C: 4, D: 5 };
  const discType = persona.disc_profile.primary_type;
  return {
    id: `saved:${persona.id}`,
    displayName: persona.name,
    voiceId: '',
    openaiVoiceId: persona.openai_voice_id,
    description: persona.disc_profile.job_title || 'Custom Persona',
    stylePreset: persona.style_preset as BuyerProfile['stylePreset'],
    avatar: '',
    difficulty: DISC_DIFFICULTY_MAP[discType] ?? 3,
    testPhrase: `Hi, I'm ${persona.name}. Let's see what you've got.`,
    detailedDescription: persona.disc_profile.buyer_insight || '',
    tips: persona.disc_profile.selling_recommendations || [],
  };
};

// Get voice ID with fallback (for ElevenLabs)
export const getVoiceId = (profile: BuyerProfile): string => {
  return profile.voiceId || DEFAULT_VOICE_ID;
};

// Get OpenAI voice ID with fallback
export const getOpenAIVoiceId = (profile: BuyerProfile): string => {
  return profile.openaiVoiceId || DEFAULT_OPENAI_VOICE_ID;
};

// Get initials for avatar fallback
export const getBuyerInitials = (profile: BuyerProfile): string => {
  return profile.displayName.split(' ').map(n => n[0]).join('').toUpperCase();
};

// Get the default MTSU buyer profile ID for a given scenario round
export const getMTSUDefaultBuyerForScenario = (scenarioId?: string): string | null => {
  if (!scenarioId) return null;
  const map: Record<string, string> = {
    'mtsu-oracle-round-1': 'mtsu-pat-washington',
    'mtsu-oracle-round-2': 'mtsu-kim-yan',
    'mtsu-oracle-round-3': 'mtsu-rafael-bonilla',
  };
  return map[scenarioId] || null;
};

// Get the default buyer profile ID for Comparion (MTSU MKT 3840) scenarios — always Anne
export const getComparionDefaultBuyer = (scenarioId?: string): string | null => {
  if (!scenarioId) return null;
  return scenarioId.startsWith('comparion-') ? 'anne-comparion' : null;
};
